<template>
  <page-component id="webiteminfo">
    <div
      v-for="item in filteredSites"
      :key="item.itemId">
      <h1
        id="webiteminfo-title"
        class="header page-header">
        {{ item.itemDescription }}
      </h1>
      <div
        class="page-subheader"
        v-if="parentdescription !== undefined && parentdescription !== ''">
        <a
          v-if="item.isNightly"
          :href="baseHref + 'siterentals.html?Location=' + item.location">
          {{ parentdescription }}
        </a>
        <span v-else>{{ parentdescription }}</span>
      </div>
      <div
        id="webiteminfo-content"
        class="twocolumn-layout">
        <div
          v-if="item.itemImage || detailsHighlight.length > 0"
          id="webiteminfo-left"
          class="group twocolumn-layout__left highlight">
          <button
            :id="item.itemId + '_image'"
            v-if="item.itemImage"
            aria-label="Open image in a dialog"
            @click="onImageClick(item.itemImage, item.itemDescription)">
            <img
              class="image"
              id="webiteminfo-image"
              alt=""
              :src="imagePath + item.itemImage" />
          </button>
          <div class="rect group__inner">
            <div
              class="newline newline-paragraph"
              v-for="detail in detailsHighlight"
              :key="detail.order">
              <div
                :id="`webiteminfo-${detail.detailorder}`"
                :class="detail.detailclass"
                v-html="detail.detailtext"></div>
            </div>
          </div>
        </div>
        <div
          id="webiteminfo-right"
          class="group twocolumn-layout__right">
          <div class="rect group__inner">
            <div class="singleLine">
              <div
                v-for="detail in detailsSingle"
                :key="detail.order">
                <div
                  :id="`webiteminfo-${detail.detailorder}`"
                  :class="detail.detailclass"
                  v-html="detail.detailtext"></div>
              </div>
            </div>
            <br />
            <div class="multiLine">
              <div
                class="newline newline-paragraph"
                v-for="detail in detailsDouble"
                :key="detail.order">
                <div
                  :id="`webiteminfo-${detail.detailorder}`"
                  :class="detail.detailclass"
                  v-html="detail.detailtext"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <group-component
        v-if="item.isNightly"
        header="">
        <div id="webiteminfo-grid">
          <div id="webiteminfo-controls">
            <div id="webiteminfo-controls-top">
              <div id="webiteminfo-controls-top-left">
                <datepicker-component
                  id="webiteminfo-checkindate"
                  label="Check-In Date"
                  :disabled="processing"
                  :minDate="currentDate"
                  :modelValue="displayedCheckInDate"
                  @change="onCheckInDate($event)">
                </datepicker-component>
                <datepicker-component
                  id="webiteminfo-checkoutdate"
                  label="Check-Out Date"
                  :disabled="processing"
                  :minDate="currentDate"
                  :maxDate="maxdate"
                  :openRight="true"
                  :modelValue="displayedCheckOutDate"
                  @change="onCheckOutDate($event)">
                </datepicker-component>
              </div>
              <div id="webiteminfo-controls-top-right">
                <button
                  id="webiteminfo-refresh"
                  type="button"
                  class="button"
                  title="Refresh"
                  aria-label="Refresh"
                  :disabled="processing"
                  @click.prevent="onRefresh()">
                  <div class="icon">
                    <span>
                      <svgicon
                        role="presentation"
                        original
                        data="@iconPath/glyph/16/refresh.svg?inline"
                        width="16"
                        height="16"></svgicon>
                    </span>
                  </div>
                  Refresh
                </button>
                <button
                  id="webiteminfo-prevmonth"
                  type="button"
                  class="button primary"
                  title="Previous Month"
                  aria-label="Previous Month"
                  :disabled="processing"
                  @click.prevent="onPrevNext(-1)">
                  Prev Month
                </button>
                <button
                  id="webiteminfo-nextmonth"
                  type="button"
                  class="button primary"
                  title="Next Month"
                  aria-label="Next Month"
                  :disabled="processing"
                  @click.prevent="onPrevNext(1)">
                  Next Month
                </button>
              </div>
            </div>
          </div>
          <div id="webiteminfo-calenders">
            <div
              v-for="month in monthColumns"
              :key="month"
              :id="`webiteminfo-calender-${month.cal}`"
              class="webiteminfo-calender">
              <table class="rental-calendar-table">
                <caption class="sr-only">
                  Availability calendar for
                  {{
                    month.desc
                  }}
                  {{
                    month.year
                  }}
                </caption>
                <thead ref="tableHead">
                  <tr>
                    <th
                      colspan="7"
                      class="month">
                      {{ month.desc }} {{ month.year }}
                    </th>
                  </tr>
                  <tr>
                    <th
                      v-for="column in dayColumns"
                      :key="column"
                      class="day"
                      :aria-label="column.long">
                      {{ column.short }}
                    </th>
                  </tr>
                </thead>
                <tbody
                  v-if="loadingCalendar || processing"
                  class="calendarLoading"
                  title="Loading Results"
                  aria-label="Loading Results">
                  <tr
                    v-for="(week, index) in weeks[month.cal]"
                    :key="index">
                    <td
                      v-if="index === 2"
                      colspan="7">
                      Loading Results
                    </td>
                    <td
                      v-else
                      v-for="(day, dayIndex) in week"
                      :key="day.key"
                      :class="{
                        last:
                          !weeks[month.cal][index + 1] ||
                          weeks[month.cal][index + 1].length <= dayIndex
                      }"></td>
                  </tr>
                </tbody>
                <tbody v-else>
                  <tr
                    v-for="(week, index) in weeks[month.cal]"
                    :key="index">
                    <td
                      v-for="day in week"
                      :key="day.key">
                      <button
                        v-if="day.date"
                        type="button"
                        :id="
                          'webiteminfo-cal-button-' +
                          month.cal +
                          '-day-' +
                          day.date.getDate()
                        "
                        :ref="day.date.toISOString()"
                        :aria-label="
                          day.dateString +
                          ' - Site ' +
                          item.itemCode +
                          ' is ' +
                          (day && item.endDate === day.dateTime
                            ? 'Selected - Click to remove selection'
                            : day &&
                              item.beginDate <= day.dateTime &&
                              item.endDate > day.dateTime
                            ? 'Selected'
                            : day.status === 'R'
                            ? 'Reserved'
                            : day.status === 'U'
                            ? 'Unavailable'
                            : day.status === 'A'
                            ? 'Available'
                            : '')
                        "
                        :class="{
                          day: true,
                          available: day.status === 'A',
                          unavailable: day.status === 'U',
                          reserved: day.status === 'R',
                          selected:
                            day &&
                            item.beginDate <= day.dateTime &&
                            item.endDate >= day.dateTime,
                          begin: day && item.beginDate === day.dateTime,
                          end: day && item.endDate === day.dateTime
                        }"
                        :disabled="day.disabled"
                        @click="onClickCalendar(item, day)">
                        <span class="rental-calendar-date">{{
                          day.date.getDate()
                        }}</span>
                        {{ day.status }}
                        <div
                          v-if="day && item.endDate === day.dateTime"
                          class="icon">
                          <span>
                            <svgicon
                              original
                              data="@iconPath/glyph/12/close.svg?inline"
                              width="10"
                              height="10"></svgicon>
                          </span>
                        </div>
                      </button>
                      <span
                        v-else
                        class="empty-cell"
                        aria-hidden="true">
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </group-component>
    </div>
    <teleport to="#app">
      <form
        ref="form"
        id="webiteminfo-form"
        class="novalidate"
        :action="baseHref + 'addtocart.html?ProcessMultiSelect=yes'"
        method="post"
        @submit.prevent="submitForm()">
        <input
          type="hidden"
          name="Action"
          value="" />
        <input
          type="hidden"
          name="SubAction"
          value="" />
        <input
          type="hidden"
          name="_csrf_token"
          :value="csrf" />
        <input
          type="hidden"
          name="Module"
          value="RN" />
        <input
          v-if="items[0].isNightly"
          type="hidden"
          name="FromProgram"
          value="siterentals" />
        <input
          v-if="items[0].isNightly"
          type="hidden"
          name="PreviousSearch"
          :value="'location=' + items[0].location" />
        <multiselectlist-component
          id="webiteminfo-multiselectlist"
          :disabled="processing || selections.length === 0"
          v-model="selections"
          :descriptions="descriptions"
          :selectionChangeMessage="selectionChangeMessage"
          @change="clearSelections()"
          @submitForm="submitForm()">
        </multiselectlist-component>
      </form>
    </teleport>
  </page-component>
</template>

<script>
import { getFormattedDate } from '@/mixins/utils'
import { siteRentals } from '@/mixins/siteRentals'

export default {
  name: 'ItemInfo',
  props: {
    parentdescription: String,
    items: Array,
    details: Array,
    initselectionstate: String,
    startdate: String,
    maxdate: String,
    initcheckindate: String,
    initcheckoutdate: String
  },
  mixins: [getFormattedDate, siteRentals],
  data() {
    return {
      csrf: App.settings.CSRFToken,
      baseHref: App.settings.BaseHref,
      imagePath: App.settings.ImagePath,
      dayColumns: [
        { short: 'Sun', long: 'Sunday' },
        { short: 'Mon', long: 'Monday' },
        { short: 'Tue', long: 'Tuesday' },
        { short: 'Wed', long: 'Wednesday' },
        { short: 'Thu', long: 'Thursday' },
        { short: 'Fri', long: 'Friday' },
        { short: 'Sat', long: 'Saturday' }
      ],
      monthColumns: [],
      weeks: [],
      beginDate: null,
      currentDate: null
    }
  },
  computed: {
    detailsHighlight() {
      const filteredDetails = this.details
      return filteredDetails
        .filter((d) => d.detailtype === 'highlight')
        .sort((a, b) => {
          if (a.detailorder > b.detailorder) return 1
          if (a.detailorder < b.detailorder) return -1
          return 0
        })
    },
    detailsSingle() {
      const filteredDetails = this.details
      return filteredDetails
        .filter((d) => d.detailtype === 'single')
        .sort((a, b) => {
          if (a.detailorder > b.detailorder) return 1
          if (a.detailorder < b.detailorder) return -1
          return 0
        })
    },
    detailsDouble() {
      const filteredDetails = this.details
      return filteredDetails
        .filter((d) => d.detailtype === 'double')
        .sort((a, b) => {
          if (a.detailorder > b.detailorder) return 1
          if (a.detailorder < b.detailorder) return -1
          return 0
        })
    }
  },
  created() {
    this.filteredSites = this.items

    if (this.items[0].isNightly) {
      this.beginDate = this.startdate
      this.currentDate = getFormattedDate(new Date())

      if (this.initcheckindate) {
        this.checkInDate = this.initcheckindate
        this.displayedCheckInDate = this.checkInDate
      }

      if (this.initcheckoutdate) {
        this.checkOutDate = this.initcheckoutdate
        this.displayedCheckOutDate = this.checkOutDate
      }

      if (new Date(this.beginDate) < new Date(this.currentDate)) {
        this.beginDate = this.currentDate
      }

      let cookie = this.getSiteRentalsCookie()
      if (cookie == null) {
        this.setSiteRentalsCookie()
      } else {
        this.beginDate = cookie[0]
        this.checkInDate = cookie[1]
        this.displayedCheckInDate = this.checkInDate
        this.checkOutDate = cookie[2]
        this.displayedCheckOutDate = this.checkOutDate
      }

      this.filteredSites.forEach((site) => {
        site.siteId = site.itemId
        site.siteCode = site.itemCode
        site.siteDescription = site.itemDescription
        site.beginTimeIntList = site.beginTimeIntList.split(',')
        site.endTimeIntList = site.endTimeIntList.split(',')
      })

      this.fetchSiteData(true)
    }

    this.initSelections()
  },
  mounted() {},
  watch: {},
  unmounted() {},
  methods: {
    onPrevNext(months) {
      if (!months) {
        return
      }

      this.loadingCalendar = true

      const tempCurrent = new Date(this.currentDate)
      const oldBeginDate = new Date(this.beginDate)
      let newBeginDate = new Date(this.beginDate)

      newBeginDate.setMonth(newBeginDate.getMonth() + months)
      if (newBeginDate.getDate() !== oldBeginDate.getDate()) {
        newBeginDate.setDate(0)
      }

      if (newBeginDate < tempCurrent) {
        newBeginDate = this.currentDate
      } else {
        newBeginDate = getFormattedDate(newBeginDate)
      }

      if (this.beginDate !== newBeginDate) {
        this.beginDate = newBeginDate
        this.fetchSiteData(true)
      } else {
        setTimeout(() => {
          this.loadingCalendar = false
        }, 300)
      }

      this.setSiteRentalsCookie()

      return months
    },
    updateDisplayedDates() {
      const monthsOfYear = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December'
      ]
      const begDate = new Date(this.beginDate)
      let oldBegDate = new Date(this.beginDate)

      this.monthColumns = []
      this.weeks = []
      this.weeks[1] = []
      this.weeks[2] = []
      this.weeks[3] = []
      this.displayedDates = []

      this.monthColumns.push({
        desc: monthsOfYear[begDate.getMonth()],
        year: begDate.getFullYear(),
        cal: 1
      })

      this.buildSelectedOptions(begDate, 1)

      begDate.setMonth(begDate.getMonth() + 1)
      if (begDate.getDate() !== oldBegDate.getDate()) {
        begDate.setDate(0)
        oldBegDate = begDate
      }

      this.monthColumns.push({
        desc: monthsOfYear[begDate.getMonth()],
        year: begDate.getFullYear(),
        cal: 2
      })

      this.buildSelectedOptions(begDate, 2)

      begDate.setMonth(begDate.getMonth() + 1)
      if (begDate.getDate() !== oldBegDate.getDate()) {
        begDate.setDate(0)
      }

      this.monthColumns.push({
        desc: monthsOfYear[begDate.getMonth()],
        year: begDate.getFullYear(),
        cal: 3
      })

      this.buildSelectedOptions(begDate, 3)
    },
    buildSelectedOptions(builddate, cal) {
      if (!(builddate instanceof Date)) return builddate

      let i = 0
      const firstOfMonth = this.normalizeDate(
        new Date(builddate.getFullYear(), builddate.getMonth(), 1)
      )

      let d = null
      for (i = 1; i <= 6; i += 1) {
        let wd = 0
        let week = []

        for (wd = 0; wd <= 6; wd += 1) {
          if (i === 1 && firstOfMonth.getDay() === wd && !d) {
            d = this.normalizeDate(new Date(firstOfMonth.getTime()))
          } else if (d) {
            d.setDate(d.getDate() + 1)
            if (d.getMonth() !== firstOfMonth.getMonth()) {
              d = null
            }
          }

          if (i === 6 && !d) {
            continue
          }

          let day = {
            key: `week${i}_weekday${wd}`,
            status: '',
            disabled: false
          }

          if (d) {
            day.date = this.normalizeDate(new Date(d.getTime()))
            day.dateString = getFormattedDate(d)
            ;(day.dateTime = d.getTime()), (day.status = 'U')
            day.disabled = this.isDateDisabled(d)

            this.displayedDates.push({
              dateString: getFormattedDate(d)
            })
          }

          week.push(day)
        }

        this.weeks[cal].push(week)
      }
      return true
    },
    normalizeDate(d) {
      if (!(d instanceof Date)) d = new Date()
      return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
    },
    isDateDisabled(d) {
      if (!(d instanceof Date)) return true

      // Min/Max Date Ranges
      return (
        d.getTime() <
          this.normalizeDate(new Date(this.currentDate)).getTime() ||
        d.getTime() > this.normalizeDate(new Date(this.maxdate)).getTime()
      )
    },
    onImageClick(itemImage, desc) {
      const URL = itemImage != null ? this.imagePath + itemImage : null
      const title = desc

      if (URL) {
        // Add resize event in case screen size changes
        if (!this.__resizeEvent) {
          $(window).on('resize', function () {
            let $dialog = $('#imagedialog')
            if ($dialog.length > 0) {
              $dialog.dialog('option', {
                width:
                  $(window).width() > 1000 ? 1000 : $(window).width() * 0.9,
                maxWidth: $(window).width() * 0.9
              })
            }
          })
        }

        const $imageDialog = $(
          '<div id="imagedialog">' +
            '<img class="imagedialog-image" src="' +
            URL +
            '" >' +
            '</div>'
        ).appendTo($('body'))

        $imageDialog.dialog({
          title: title,
          resizable: false,
          draggable: false,
          modal: true,
          width: $(window).width() > 830 ? 830 : $(window).width() * 0.9,
          maxWidth: $(window).width() * 0.9,
          height: 800,
          close: function () {
            $(this).dialog('destroy').remove()
          }
        })

        return false
      }
    },
    changeData(newFilteredSites) {
      this.filteredSites = newFilteredSites
      this.filteredSites.forEach((site) => {
        for (let i = 1; i <= 3; i++) {
          this.weeks[i].forEach((w) => {
            w.forEach((d) => {
              const siteDate = site.dates.find(
                (sd) => sd.dateString === d.dateString
              )

              if (siteDate) d.status = siteDate.status
            })
          })
        }
      })

      return
    },
    onClickCalendar(site, day) {
      if (this.processing) return

      let removed = false
      let newBeginDate = null
      let newEndDate = null
      let nextLastValues = []
      let nextDateTime = 0
      let lastDateTime = 0

      if (!site.beginDate) {
        if (day.status !== 'A') return

        nextLastValues = this.getNextLastValues(day.dateTime)
        nextDateTime = nextLastValues[0]
        lastDateTime = nextLastValues[1]

        if (day.dateTime === lastDateTime) {
          this.onPrevNext(1)

          nextLastValues = this.getNextLastValues(day.dateTime)
          nextDateTime = nextLastValues[0]
        }

        newBeginDate = day.dateTime

        if (this.checkInDate && this.checkOutDate) {
          const tempInDate = new Date(this.checkInDate)
          const tempOutDate = new Date(this.checkOutDate)

          if (
            tempInDate < tempOutDate &&
            newBeginDate === tempInDate.getTime()
          ) {
            newEndDate = tempOutDate.getTime()
          } else {
            newEndDate = nextDateTime
          }
        } else {
          newEndDate = nextDateTime
        }
      } else if (site.endDate === day.dateTime) {
        newBeginDate = site.beginDate
        newEndDate = site.endDate
        removed = true
      } else if (site.endDate < day.dateTime) {
        newBeginDate = site.beginDate
        newEndDate = day.dateTime
      } else if (site.beginDate > day.dateTime) {
        newBeginDate = day.dateTime
        newEndDate = site.endDate
      }

      this.makeSelection(site, removed, newBeginDate, newEndDate)
    },
    getNextLastValues(dayDateTime) {
      let useNext = false
      let next = dayDateTime
      let last = 0

      for (let i = 1; i <= 3; i++) {
        this.weeks[i].forEach((w) => {
          w.forEach((d) => {
            if (useNext && d.dateTime && next === dayDateTime) {
              next = d.dateTime
            } else if (d.dateTime === next) useNext = true

            if (d.dateTime) last = d.dateTime
          })
        })
      }

      return [next, last]
    }
  }
}
</script>

<style lang="less" scoped>
@import '../../assets/css/abstract/variables';
@import '../../assets/css/abstract/mixins';
@import '../../assets/css/components/table';

.default-focus-indicator(0, black);

.page-subheader {
  margin-top: 8px;
  font-size: 16px;
  font-weight: bold;
  a,
  span {
    font-size: 16px;
    font-weight: bold;
  }
}

#webiteminfo-content {
  padding-top: 7px;
  padding-bottom: 0px;

  .singleLine {
    display: flex;
  }

  .twocolumn-layout__right,
  .twocolumn-layout__left {
    margin-bottom: 0px;
  }

  .twocolumn-layout__right {
    max-width: calc(1360px - 290px - 14px);
  }

  div:only-child.twocolumn-layout__right {
    max-width: 100%;
  }

  .highlight {
    background-color: #f2f2f2;
    margin-right: 16px;
    min-width: 290px;

    img {
      width: 100%;
    }
  }
}

.webiteminfo .image {
  max-width: 100%;
}

#webiteminfo-grid {
  display: flex;
  flex-flow: column;
  width: 100%;
  float: left;

  #webiteminfo-controls {
    width: 100%;
    float: left;
    margin-bottom: 10px;

    #webiteminfo-controls-top {
      width: 100%;
      padding: 12px;
      margin-bottom: 12px;
      float: left;
      background: @background-color;
      border-radius: 4px;
      border: solid 1px rgba(192, 192, 192, 0.5);

      #webiteminfo-controls-top-left {
        float: left;

        .datepicker-wrap {
          float: left;
          width: 130px;
          margin-right: 12px;
        }
      }

      #webiteminfo-controls-top-right {
        float: right;

        button {
          margin: 27px 0 0 12px;
          padding: 0 12px 0 12px;
          float: left;
          position: relative;
        }

        #webiteminfo-refresh {
          text-align-last: center;
          padding-left: 30px;
        }
      }
    }

    .primary {
      .default-focus-indicator(3px, #005486);
      background-color: #015c8e;
      border-color: #015c8e;
      color: #fff;

      &:hover,
      &:focus {
        background-color: #005486;
      }
    }

    .icon {
      position: absolute;
      top: 25%;
      left: 5px;
      width: 16px;
      height: 16px;

      .svg-icon {
        position: absolute;
        top: 50%;
        left: 50%;
        margin-top: -8px;
        margin-left: -5px;
      }
    }
  }

  #webiteminfo-calenders {
    display: inline-flex;
    justify-content: center;
  }

  .webiteminfo-calender {
    float: left;
    margin-left: 16px;

    .calendarLoading {
      td {
        border-right: solid 1px @controls-border-color;
        border-bottom: solid 1px @controls-border-color;

        &:first-child {
          border-left: solid 1px @controls-border-color;
        }

        &:not(.last) {
          border-bottom: 0px;
        }

        &:not(:last-child) {
          border-right: 0px;
        }
      }
    }

    &:first-child {
      margin-left: 0;
    }

    th {
      overflow: hidden;
      color: #272c33;
      font-weight: 600;
      background: #fff;
      font-size: @font-size-14px;
      position: relative;
      width: 49px;
      height: 40px;
      padding: 0;
      text-align: center;
      line-height: 1.1;
    }

    th.month {
      border-top: solid 1px @controls-border-color;
      border-left: solid 1px @controls-border-color;
      border-right: solid 1px @controls-border-color;
    }

    th.day {
      background: #46545f;
      color: #fff;
      border-right: solid 1px rgba(192, 192, 192, 0.5);
      min-width: 24px;
    }

    td {
      background: #fff;
      color: #272c33;
      font-size: @font-size-14px;
      height: 49px;
      padding: 0;
      text-align: center;
      vertical-align: middle;

      a,
      a:link,
      a:active,
      a:hover {
        font-size: @font-size-14px;
      }

      a:focus {
        outline: 2px solid #000;
      }

      a.fill-cell {
        width: 100%;
      }

      &.no-wrap {
        white-space: nowrap;
      }

      &.button-cell {
        &--more {
          overflow: visible;
        }
      }

      &:first-child {
        button,
        .empty-cell {
          border-left: solid 1px #015c8e;
        }
      }

      button {
        background: transparent;
        text-align: center;
        position: relative;
        width: 100%;
        height: 100%;
        cursor: pointer;
        border-right: 1px solid #015c8e;
        border-bottom: 1px solid #015c8e;

        .rental-calendar-date {
          position: absolute;
          top: 2px;
          left: 4px;
          color: #272c33;
          font-size: @font-size-12px;
          font-weight: 700;
        }

        .icon {
          position: absolute;
          top: 2px;
          right: -5px;
          width: 16px;
          height: 16px;
          background: #e60013;
          border-radius: 100%;
          z-index: 2;

          .svg-icon {
            position: absolute;
            top: 50%;
            left: 50%;
            margin-top: -5px;
            margin-left: -5px;
          }
        }

        &:focus {
          outline: 2px solid #000;
          z-index: 1;
          border: 0;
        }
      }

      .empty-cell {
        display: block;
        width: 100%;
        height: 100%;
        border-right: 1px solid #015c8e;
        border-bottom: 1px solid #015c8e;
      }
    }

    .available {
      background-color: #e6f2f8;
      color: #015c8e;
      font-weight: 700;
    }

    .unavailable,
    .reserved {
      color: #272c33;
    }

    button:disabled {
      color: light-dark(rgba(16, 16, 16, 0.3), rgba(255, 255, 255, 0.3));

      span {
        color: light-dark(rgba(16, 16, 16, 0.3), rgba(255, 255, 255, 0.3));
      }
    }

    .day.available:hover,
    .day.available:focus {
      background-color: #cfdadf;
    }

    .day.selected {
      // using font-size 0 here to get rid of the contrast ratio error for these cells (the status is displayed when the cell is selected but it has the same color as the background so it's not visible anyway)
      font-size: 0;
      background-color: #015c8e;
      color: #015c8e;
      border-color: #015c8e;
      box-shadow: 1px 0 0 #015c8e;
    }

    .day.selected:hover,
    .day.selected:focus {
      background-color: #015380;
      color: #015380;
    }

    .day.selected.begin {
      box-shadow: 1px 0 0 #015c8e, inset 4px 0 0 #000;
    }

    .day.selected.end {
      box-shadow: inset -4px 0 0 #000;
      border-right: none;
    }

    .day.selected .rental-calendar-date {
      display: none;
    }
  }
}

@media (max-width: 992px) {
  #webiteminfo-content {
    padding-top: 4px;

    .highlight {
      margin-bottom: -1px;
      margin-right: 0px;
    }

    .twocolumn-layout__right {
      margin-right: 0px;
    }
  }

  #webiteminfo-grid {
    width: 100% !important;

    #webiteminfo-controls {
      #webiteminfo-controls-top {
        width: 100% !important;
        display: block !important;

        .datepicker-wrap {
          width: 48% !important;
          margin: 0 !important;

          &:first-child {
            margin-right: 4% !important;
          }
        }

        #webiteminfo-controls-top-left {
          width: 100%;
        }

        #webiteminfo-controls-top-right {
          width: 100%;

          button {
            margin: 8px 0 0 0 !important;
            width: 49% !important;
            padding: 0 2px !important;
            line-height: 1;
          }

          #webiteminfo-refresh {
            width: 100% !important;
          }

          #webiteminfo-nextmonth {
            margin-left: 2% !important;
          }
        }
      }
    }

    #webiteminfo-calender-2,
    #webiteminfo-calender-3 {
      display: none;
    }
  }
}
</style>
